import { Button, Divider, Form, Spin } from 'antd';
import type { FormInstance } from 'antd/es/form';
import logo from '../../assets/Logo.png';
import './index.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ScoreCardImage from '../../assets/ScoreCardImage.svg';
import { FloatInput } from '../../components/Fields/FloatInput';
import {
    ScoreCardEvaluationCheckResponse,
    tendersApi,
    useLazyCheckScoreCardEvaluationQuery,
    useLazyGetScoreCardQuery
} from '../../api/tenders';
import { defaultSerializeQueryArgs, type EndpointDefinition, type QueryCacheKey } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ExistingScoreCard } from './ExistingScoreCard';
import { ScoreCard } from './ScoreCard';
import { ScoreCardCompletedModal } from './ScoreCardCompletedModal';
import { ScoreCardSubmittedView } from './ScoreCardSubmittedView';
import { Loader } from '../../components/Loader/Loader';

function ScoreCardWelcomeStep({
    form,
    onFinish,
    checkScoreCardEvaluationData,
    isScoreCardLoading
}: {
    form: FormInstance;
    onFinish: (values: { name: string }) => void;
    checkScoreCardEvaluationData: ScoreCardEvaluationCheckResponse | undefined;
    isScoreCardLoading: boolean;
}) {
    return (
        <div className="SupplierCompanyPitches__success success">
            <div className="success__block">
                <div className="preferences-submitted">
                    <h1 className="preferences-submitted__title">Welcome to the Supplier Scorecard</h1>

                    <p className="preferences-submitted__text">
                        Please complete the scorecard based on the supplier&apos;s pitch for the supplier and opportunity
                        shown below.
                    </p>

                    <div className="score-card-info">
                        <div className="score-card-info__row">
                            <div className="score-card-info__label">Supplier</div>
                            <div className="score-card-info__value">
                                {checkScoreCardEvaluationData?.data?.company?.name}
                            </div>
                        </div>
                        <Divider />
                        <div className="score-card-info__row">
                            <div className="score-card-info__label">Opportunity</div>
                            <div className="score-card-info__value">
                                <div>{checkScoreCardEvaluationData?.data?.bayer?.city}</div>
                                <div className="score-card-info__sub-value">
                                    {checkScoreCardEvaluationData?.data?.bayer?.province}
                                </div>
                            </div>
                        </div>
                    </div>
                    <br />
                    <p className="preferences-submitted__text">Please enter your full name to begin the evaluation.</p>
                    <p className="preferences-submitted__text" style={{ color: '#4D5E93' }}>
                        Your full name is used to identify evaluators and allow scoring to be viewed and analysed by
                        evaluator.
                    </p>
                    <Form form={form} onFinish={onFinish} style={{ marginTop: '24px' }}>
                        <Form.Item name="name" rules={[{ required: true, message: 'This is a required field' }]}>
                            <FloatInput floatLabel="Full Name" className="input" maxLength={128} />
                        </Form.Item>
                        <Button
                            htmlType="submit"
                            type="primary"
                            loading={isScoreCardLoading}
                            style={{ width: '100%', marginTop: '24px' }}
                        >
                            {isScoreCardLoading ? <Loader /> : 'Begin Evaluation'}
                        </Button>
                    </Form>
                </div>
            </div>
            <img src={ScoreCardImage} className="image__item" alt="" />
        </div>
    );
}

function buildGetScoreCardQueryCacheKey(token: string, user_name: string) {
    const ep = tendersApi.endpoints.getScoreCard as unknown as EndpointDefinition<any, any, any, any>;
    return defaultSerializeQueryArgs({
        queryArgs: { token, user_name },
        endpointDefinition: ep,
        endpointName: tendersApi.endpoints.getScoreCard.name
    });
}

export const ScoreCardPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [existingScoreCard, setExistingScoreCard] = useState(false);
    const [evaluationSubmitted, setEvaluationSubmitted] = useState(false);
    const [completedEvaluationNoticeOpen, setCompletedEvaluationNoticeOpen] = useState(false);
    /** After a completed evaluation, ScoreCard is shown only after the user confirms in the modal */
    const [resumeScoreCardAfterNotice, setResumeScoreCardAfterNotice] = useState(false);

    const [form] = Form.useForm();

    const [getScoreCard, { data, isLoading: isScoreCardLoading }] = useLazyGetScoreCardQuery();
    const [
        checkScoreCardEvaluation,
        { data: checkScoreCardEvaluationData, isFetching: isCheckEvaluationFetching, error, isError }
    ] = useLazyCheckScoreCardEvaluationQuery();

    useEffect(() => {
        if (isError && error) {
            navigate('/expired');
        }
    }, [error, isError]);

    useEffect(() => {
        if (!token) return;
        checkScoreCardEvaluation(token);
    }, [token, checkScoreCardEvaluation]);

    const resetGetScoreCardQueryForFormName = () => {
        const userNameRaw = form.getFieldValue('name');
        const user_name = typeof userNameRaw === 'string' ? userNameRaw.trim() : '';
        if (!token || !user_name) return;
        dispatch(
            tendersApi.internalActions.removeQueryResult({
                queryCacheKey: buildGetScoreCardQueryCacheKey(token, user_name) as QueryCacheKey
            })
        );
    };

    const handleSubmit = (values: { name: string }) => {
        if (!token) return;

        void getScoreCard({ token, user_name: values.name.trim() })
            .unwrap()
            .then((result: unknown) => {
                const r = result as { progressData?: { completed?: boolean } } | undefined;
                if (r?.progressData?.completed === true) {
                    setResumeScoreCardAfterNotice(false);
                    setCompletedEvaluationNoticeOpen(true);
                }
            })
            .catch(() => {});
    };

    useEffect(() => {
        setEvaluationSubmitted(false);
    }, [data?.formData?.id]);

    useEffect(() => {
        if (data?.progressData?.completed === true) {
            setResumeScoreCardAfterNotice(false);
            setCompletedEvaluationNoticeOpen(true);
        } else if (data?.progressData && data.progressData.completed === false) {
            setCompletedEvaluationNoticeOpen(false);
            setResumeScoreCardAfterNotice(false);
        }
    }, [data?.formData?.id, data?.progressData?.completed]);

    const completedEvaluationPreviously = data?.progressData?.completed === true;

    useEffect(() => {
        const nameFromApi = typeof data?.user_name === 'string' ? data.user_name : '';
        if (completedEvaluationPreviously && data?.formData?.id != null && nameFromApi.trim()) {
            form.setFieldsValue({ name: nameFromApi.trim() });
        }
    }, [completedEvaluationPreviously, data?.user_name, data?.formData?.id, form]);

    return isCheckEvaluationFetching ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Spin />
        </div>
    ) : (
        <>
            <div className="SupplierCompanyPitches">
                <div className="SupplierCompanyPitches__logowrap">
                    <img src={logo} className="SupplierCompanyPitches__logo" />
                </div>

                {data ? (
                    evaluationSubmitted ? (
                        <ScoreCardSubmittedView />
                    ) : completedEvaluationPreviously && !resumeScoreCardAfterNotice ? (
                        <>
                            <ScoreCardCompletedModal
                                open={completedEvaluationNoticeOpen}
                                onClose={() => {
                                    setCompletedEvaluationNoticeOpen(false);
                                    resetGetScoreCardQueryForFormName();
                                }}
                                onConfirmSubmit={() => {
                                    setCompletedEvaluationNoticeOpen(false);
                                    setResumeScoreCardAfterNotice(true);
                                }}
                                supplierName={checkScoreCardEvaluationData?.data?.company?.name ?? '—'}
                                evaluatorName={typeof data?.user_name === 'string' ? data.user_name : ''}
                            />
                            <ScoreCardWelcomeStep
                                form={form}
                                onFinish={handleSubmit}
                                checkScoreCardEvaluationData={checkScoreCardEvaluationData}
                                isScoreCardLoading={isScoreCardLoading}
                            />
                        </>
                    ) : (
                        <ScoreCard
                            data={data}
                            token={token}
                            onSubmitted={() => setEvaluationSubmitted(true)}
                            checkScoreCardEvaluationData={checkScoreCardEvaluationData}
                        />
                    )
                ) : (
                    <ScoreCardWelcomeStep
                        form={form}
                        onFinish={handleSubmit}
                        checkScoreCardEvaluationData={checkScoreCardEvaluationData}
                        isScoreCardLoading={isScoreCardLoading}
                    />
                )}
            </div>
            {existingScoreCard && (
                <ExistingScoreCard openModal={existingScoreCard} setOpenModal={setExistingScoreCard} />
            )}
        </>
    );
};
